/**
 * 項目分類（itemCategory / itemCategories / objectType）の正規化ユーティリティ。
 *
 * 移植元: poc_test_designer/src/lib/resolve-target-applicability.ts の
 * item-category 関連関数のみを抜粋（lens/viewpoints 判定は spec text 生成に不要なため除外）。
 * spec text の出力を poc_test_designer と一致させるため、ロジックは原本と等価に保つこと。
 */
import type { ItemCategoryEntry, ItemItemCategory } from "../../types/TestTarget";

const ITEM_CATEGORIES: ReadonlySet<string> = new Set([
  "Display",
  "Action",
  "Input",
  "Selection",
]);

export interface ResolvedItemCategoryEntry {
  objectType?: string;
  itemCategory: ItemItemCategory;
  itemSubCategory?: string;
}

/**
 * legacy params.type から部品種別を推定（useAllConditions.inferItemType と同等）。
 */
export function inferLegacyItemSubCategory(
  params: Record<string, unknown>
): string | undefined {
  const itemSub = params.itemSubCategory;
  if (typeof itemSub === "string" && itemSub.trim()) {
    return itemSub.trim();
  }
  const itemType = params.itemType;
  if (typeof itemType === "string" && itemType.trim()) {
    return itemType.trim();
  }
  const rawType = params.type;
  if (typeof rawType !== "string" || !rawType) return undefined;
  switch (rawType.toLowerCase()) {
    case "text":
      return "text";
    case "number":
      return "number";
    case "range":
    case "slider":
    case "slidebar":
      return "slidebar";
    case "date":
    case "datetime-local":
      return "date";
    case "file":
      return "file";
    case "select":
      return "select";
    case "textarea":
      return "textarea";
    case "checkbox":
      return "checkbox";
    case "radio":
      return "radio";
    case "button":
    case "link":
      return undefined;
    default:
      return undefined;
  }
}

export function parseItemCategory(value: unknown): ItemItemCategory | undefined {
  if (typeof value !== "string" || !value.trim()) return undefined;
  const trimmed = value.trim();
  if (ITEM_CATEGORIES.has(trimmed)) {
    return trimmed as ItemItemCategory;
  }
  return undefined;
}

function getNonEmptyString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function parseItemCategoryEntry(
  value: unknown
): ResolvedItemCategoryEntry | undefined {
  if (!value || typeof value !== "object") return undefined;
  const raw = value as Partial<ItemCategoryEntry> & Record<string, unknown>;
  const itemCategory = parseItemCategory(raw.itemCategory ?? raw.item_category);
  if (!itemCategory) return undefined;
  const itemSubCategory = getNonEmptyString(
    raw.itemSubCategory ?? raw.item_sub_category
  );
  const objectType = getNonEmptyString(raw.objectType ?? raw.object_type);
  return {
    ...(objectType !== undefined && { objectType }),
    itemCategory,
    ...(itemSubCategory !== undefined && { itemSubCategory }),
  };
}

function dedupeItemCategoryEntries(
  entries: ResolvedItemCategoryEntry[]
): ResolvedItemCategoryEntry[] {
  const seen = new Set<string>();
  const result: ResolvedItemCategoryEntry[] = [];
  for (const entry of entries) {
    const key = [
      entry.objectType?.toLowerCase() ?? "",
      entry.itemCategory,
      entry.itemSubCategory?.toLowerCase() ?? "",
    ].join("\u0000");
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(entry);
  }
  return result;
}

export function resolveItemCategoryEntries(
  params: Record<string, unknown> | null | undefined
): ResolvedItemCategoryEntry[] {
  const p = params ?? {};
  const rawEntries = Array.isArray(p.itemCategories)
    ? p.itemCategories
    : Array.isArray(p.item_categories)
      ? p.item_categories
      : [];
  const entries = rawEntries
    .map(parseItemCategoryEntry)
    .filter((entry): entry is ResolvedItemCategoryEntry => entry !== undefined);

  if (entries.length > 0) {
    return dedupeItemCategoryEntries(entries);
  }

  const itemCategory = parseItemCategory(p.itemCategory ?? p.item_category);
  if (!itemCategory) return [];
  const itemSubCategory = inferLegacyItemSubCategory(p);
  const objectTypes = Array.isArray(p.objectTypes)
    ? p.objectTypes
    : Array.isArray(p.object_types)
      ? p.object_types
      : [];
  const objectType = objectTypes
    .map(getNonEmptyString)
    .find((v): v is string => v !== undefined);
  return [
    {
      ...(objectType !== undefined && { objectType }),
      itemCategory,
      ...(itemSubCategory !== undefined && { itemSubCategory }),
    },
  ];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * params の項目分類を保存用の正規形に揃える。
 * - itemCategories が無い旧データでも、単一 itemCategory/itemSubCategory から 1 要素配列を補完する。
 * - 互換ミラーの itemCategory/itemSubCategory は先頭要素として残す。
 */
export function normalizeItemCategoryParams(params: unknown): unknown {
  if (!isRecord(params)) return params;
  const entries = resolveItemCategoryEntries(params);
  if (entries.length === 0) return params;

  const first = entries[0];
  const objectTypes = entries
    .map((entry) => entry.objectType)
    .filter(
      (value): value is string =>
        typeof value === "string" && value.trim().length > 0
    );

  return {
    ...params,
    itemCategory: first.itemCategory,
    ...(first.itemSubCategory !== undefined && {
      itemSubCategory: first.itemSubCategory,
    }),
    itemCategories: entries.map((entry) => ({
      ...(entry.objectType !== undefined && { objectType: entry.objectType }),
      itemCategory: entry.itemCategory,
      ...(entry.itemSubCategory !== undefined && {
        itemSubCategory: entry.itemSubCategory,
      }),
    })),
    ...(objectTypes.length > 0 && { objectTypes }),
  };
}
